import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CacheService } from './../services/caching.service';
const env = window.environments;

export const cacheInterceptor: HttpInterceptorFn = (
    req: HttpRequest<any>,
    next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
    const cacheService = inject(CacheService);

    const isCachingAvailable: boolean = cacheService.isAvailable;
    if (isCachingAvailable) {
        // project/fetch is always intercepted (never cached, but used for mode detection)
        if (req.url === `${env?.ALPHA_UI_MAKE_REQUEST_URL}?/project/fetch`) {
            return handleProjectFetch(cacheService, req, next);
        }

        // Cache is active in BOTH modes once projectId is set
        if (cacheService.projectId !== 'default') {
            // Dev mode requires manifest to be loaded
            if (cacheService.mode === 'dev' && cacheService.manifest.size === 0) {
                return next(req); // Manifest not yet loaded, skip cache
            }
            // Prod mode requires currentTag
            if (cacheService.mode === 'prod' && cacheService.currentTag === 'default') {
                return next(req);
            }
            return handleCaching(cacheService, req, next);
        }
    }
    return next(req);
};

// handles the "project/fetch" request, saves projectId & version, and invalidates cache when necessary.
const handleProjectFetch = (
    cacheService: CacheService,
    req: HttpRequest<any>,
    next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
    return next(req).pipe(
        tap(async (event) => {
            if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
                await cacheService.setProjectCred(event.body);

                if (event.body?.devMode) {
                    // Dev mode: manifest is fetched by the project resolver (blocking)
                    // to ensure it's ready before any config API calls.
                    // No-op here — setProjectCred already set mode/branch.
                } else {
                    // Prod mode: existing tag-based invalidation
                    const uniqueId = `project_${event.body?.projectId}`;
                    await cacheService.invalidateCache();
                    await cacheService.setConfig({
                        key: uniqueId,
                        data: event.body,
                    });
                }
            }
        }),
    );
};

// cache all api's response from cacheConfig of particular service after version & projectId is set using project/fetch.
const handleCaching = (
    cacheService: CacheService,
    req: HttpRequest<any>,
    next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
    return new Observable((observer) => {
        (async () => {
            const { url, method, body, params } = req;

            const existingEndpoint = cacheService.findEndpoint(url, method, body);

            if (existingEndpoint) {
                const requestBodyKey = cacheService.generateRequestBodyKey(
                    { body: body?.body, params },
                    existingEndpoint,
                );

                const domain = typeof body?.body?.url === 'string' ? body?.url : body?.url?.value?.__alpha_env__;
                const uniqueId = cacheService.generateCacheKey(url, method, requestBodyKey, domain);

                try {
                    const cachedResponse = await cacheService.getConfig(uniqueId, env?.CACHE_TTL);

                    if (cachedResponse) {
                        observer.next(
                            new HttpResponse({
                                body: cachedResponse,
                                status: 200,
                            }),
                        );
                        observer.complete();
                        return;
                    }
                } catch (error) {
                    console.log('Error while fetching CachedResponse:', error);
                }

                next(req)
                    .pipe(
                        tap((event) => {
                            if (event instanceof HttpResponse) {
                                cacheService.setConfig({
                                    key: uniqueId,
                                    data: event.body,
                                });
                            }
                        }),
                    )
                    .subscribe({
                        next: (response) => observer.next(response),
                        error: (err) => observer.error(err),
                        complete: () => observer.complete(),
                    });
            } else {
                next(req).subscribe(observer);
            }
        })();
    });
};
